import { localStorageService } from '~/@common/services/storage.service'

import { INTEGRATION_KEY } from './constants'

/** scoring Map 의 키 — 학습지/QR 은 단일 number, 교재는 `${workbookProblemId}-${studentWorkbookProgressId}` 합성 string */
type ScoringId = number | `${number}-${number}`

type LegacyScoringMigrationParams = {
  /** ex: 'student-worksheet/82189794' */
  legacyKeyPrefix: string
  /**
   * problemIndex 위치별 ProblemScoring.id.
   * - 학습지/QR: 단일 worksheetProblemId (number)
   * - 교재: `${workbookProblemId}-${studentWorkbookProgressId}` 합성 키 (StudentWorkbookScoring.constructScoringId 사용)
   */
  problemIdByIndex: ReadonlyArray<ScoringId>
}

type MigratableEntry = {
  scoringId: ScoringId
  localAnswer: string
}

/** 이미 마이그레이션 처리된 교재/학습지 목록 — 다음 진입 시 재실행 방지용
 *  ['student-worksheet/82189794','student-workbook/4216913/4176351/582786851',...]
 */
const readMigratedScorings = (): string[] => {
  const raw = localStorage.getItem(INTEGRATION_KEY.SCORING_MIGRATION_DONE)
  if (raw === null) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((v): v is string => typeof v === 'string')
  } catch {
    return []
  }
}

const writeMigratedScorings = (prefixes: string[]): void => {
  localStorage.setItem(INTEGRATION_KEY.SCORING_MIGRATION_DONE, JSON.stringify(prefixes))
}

/**
 * legacy 분리 키의 raw 문자열 → 통합앱 'scoring' Map 에 넣을 답안 문자열.
 * - 객관식 복수: 콤마 join (예: ['1','2'] → '1,2')
 * - 단답/UNKNOWN/CORRECT/WRONG/주관식: 단일 element 그대로
 * - parse 실패 / 형식 불일치: null (skip 신호)
 */
const parseLegacyAnswer = (raw: string, legacyKey: string): string | null => {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch (err) {
    // silent failure 회피용 warn — 해당 entry 만 skip
    console.warn(`[legacyScoringMigration] JSON.parse 실패 — skip ${legacyKey}`, err)
    return null
  }
  // 빈 배열이 아닌 string 배열인지 — 형식 불일치(예: object) 면 skip
  if (
    !Array.isArray(parsed) ||
    parsed.length === 0 ||
    !parsed.every((v) => typeof v === 'string')
  ) {
    return null
  }
  return parsed.join(',')
}

/** legacy 키들을 훑어 유효한 답안 entry 만 수집 */
const collectMigratableEntries = (
  legacyKeyPrefix: string,
  problemIdByIndex: ReadonlyArray<ScoringId>,
): MigratableEntry[] => {
  const entries: MigratableEntry[] = []
  for (let i = 0; i < problemIdByIndex.length; i++) {
    const legacyKey = `${legacyKeyPrefix}/${i}`
    const raw = localStorage.getItem(legacyKey)
    if (raw === null) continue

    const localAnswer = parseLegacyAnswer(raw, legacyKey)
    if (localAnswer === null) continue

    entries.push({ scoringId: problemIdByIndex[i], localAnswer })
  }
  return entries
}

// ───────────────────────── main ─────────────────────────

/**
 * 구 학생앱(mathflat-student) 의 분리 키 답안을 통합 학생앱의 'scoring' Map 으로 병합한다.
 * - 학습지/교재 단위로 1회만 실행 (marker 로 idempotency 보장 — 사용자의 후속 입력/삭제를 보호)
 *
 * @returns 병합한 entry 개수 (0 이면 legacy 데이터 없거나 이미 처리됨)
 */
export function mergeLegacyScoringIntoLocalStorage(params: LegacyScoringMigrationParams): number {
  const { legacyKeyPrefix, problemIdByIndex } = params

  // 1. 이미 마이그레이션된 학습지/교재면 skip
  const migratedPrefixes = readMigratedScorings()
  if (migratedPrefixes.includes(legacyKeyPrefix)) return 0

  // 2. 유효한 legacy 답안만 수집 (parse 실패 시 receiver 보존)
  const legacyEntries = collectMigratableEntries(legacyKeyPrefix, problemIdByIndex)
  if (legacyEntries.length === 0) return 0

  // 3. scoring Map 에 replace
  // -> legacy 데이터(베이직학생앱)가 있을때만 교체하는데 베이직학생앱에 저장 데이터가 있었다면 프리미엄을 쓰지 않았을것이므로 scoring에 덮어쓰기는 거의 발생하지 않음
  for (const { scoringId, localAnswer } of legacyEntries) {
    localStorageService.removeScoringData<'WORKSHEET' | 'WORKBOOK'>({ scoringId })
    localStorageService.setScoringData<'WORKSHEET' | 'WORKBOOK'>({ scoringId, localAnswer })
  }

  // 4. 다음 진입 시 재실행 방지 marker 기록
  writeMigratedScorings([...migratedPrefixes, legacyKeyPrefix])

  return legacyEntries.length
}
